/**
 * pitchCsvDownload.ts
 * Utility for generating and downloading Apple Music and Deezer pitch CSVs.
 */

import { classifyCompanyType } from "./companyTypeClassification";

interface PitchData {
  id: string | number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  focusTrackName?: string;
  trackISRC: string;
  songDescription: string;
  mainGenre: string;
  songMood: string;
  language: string;
  featuredArtists: string;
  shortDescription: string;
  longDescription: string;
  lyrics: string;
  linkToListen: string;
  promoPhotos: string;
  status: string;
  companyName?: string;
  companyType?: string;
  AppleInfo?: Record<string, any>;
  DeezerInfo?: Record<string, any>;
  SpotifyInfo?: Record<string, any>;
  SoundCloudInfo?: Record<string, any>;
  [key: string]: any;
}

/**
 * Sanitize a string for use in CSV file names.
 * Replaces spaces with underscores and removes special characters.
 */
function sanitizeFileName(str: string): string {
  return str
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_\-]/g, "");
}

/**
 * Escape a CSV field value.
 * Wraps in double quotes if it contains commas, quotes, or newlines.
 */
function escapeCsvField(value: any): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string from headers and a row of values.
 */
function buildCsv(headers: string[], row: string[]): string {
  const BOM = "\uFEFF"; // UTF-8 BOM for Excel compatibility
  const headerLine = headers.map(escapeCsvField).join(",");
  const dataLine = row.map(escapeCsvField).join(",");
  return BOM + headerLine + "\n" + dataLine;
}

/**
 * Extract Y/M/D components from a date string without going through a UTC
 * `Date` conversion, which shifts date-only strings (e.g. "2026-08-07") back
 * a day in timezones behind UTC. Falls back to local `Date` parsing for
 * strings `Date` can parse but that aren't in YYYY-MM-DD form.
 */
function parseDateParts(dateStr: string): { year: number; month: number; day: number } | null {
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2]),
      day: Number(isoMatch[3]),
    };
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

/**
 * Format a date string to YYYY-MM-DD.
 */
function formatDateYMD(dateStr: string): string {
  if (!dateStr) return "";
  const parts = parseDateParts(dateStr);
  if (!parts) return dateStr;
  const { year, month, day } = parts;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Format a date string to DD/MM/YYYY (Deezer format).
 */
function formatDateDMY(dateStr: string): string {
  if (!dateStr) return "";
  const parts = parseDateParts(dateStr);
  if (!parts) return dateStr;
  const { year, month, day } = parts;
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}

/**
 * Trigger a file download in the browser.
 * Returns true if download was initiated, false if blocked.
 */
function triggerDownload(csvContent: string, fileName: string): boolean {
  try {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

// ─── Apple Music CSV (24 columns) ───────────────────────────────────────────

const APPLE_HEADERS = [
  "PITCH TYPE",
  "PITCH FROM",
  "PITCH TO",
  "CONTENT TITLE",
  "ARTIST NAME",
  "APPLE ID",
  "UPC",
  "FORMAT",
  "PROMOTION START DATE",
  "RELEASE DATE",
  "MEDIA LANGUAGE",
  "LABEL",
  "ARTIST ORIGIN",
  "GENRES",
  "MOODS",
  "HAS TIMED RELEASE",
  "TIMED RELEASE DATE",
  "TIMED RELEASE TIME",
  "HAS MOTION ARTWORK",
  "HAS SPATIAL AUDIO",
  "LYRICS",
  "LISTENING URL",
  "PRESS SHOOT URL",
  "PROMOTION DETAILS (MARKET DRIVERS)",
];

function buildAppleRow(pitch: PitchData): string[] {
  const apple = pitch.AppleInfo || {};
  return [
    apple.releaseStage || "",                          // PITCH TYPE
    apple.pitchFrom || "",                             // PITCH FROM
    apple.pitchTo || "",                               // PITCH TO
    pitch.releaseTitle || "",                           // CONTENT TITLE
    pitch.artistName || "",                             // ARTIST NAME
    apple.appleId || "",                               // APPLE ID
    pitch.releaseUPC || "",                             // UPC
    pitch.configuration || "",                          // FORMAT
    apple.promotionDate || "",                          // PROMOTION START DATE
    pitch.releaseDate || "",                            // RELEASE DATE
    apple.language || pitch.language || "",             // MEDIA LANGUAGE
    apple.label || "",                                  // LABEL
    apple.country || "",                                // ARTIST ORIGIN
    apple.subgenre || "",                               // GENRES
    pitch.songMood || "",                               // MOODS
    apple.hasTimedRelease || "",                        // HAS TIMED RELEASE
    apple.timedReleaseDate || "",                       // TIMED RELEASE DATE
    "",                                                 // TIMED RELEASE TIME
    apple.hasMotionArtwork || "",                       // HAS MOTION ARTWORK
    apple.hasSpatialAudio || "",                        // HAS SPATIAL AUDIO
    pitch.lyrics || "",                                 // LYRICS
    pitch.linkToListen || "",                           // LISTENING URL
    pitch.promoPhotos || "",                            // PRESS SHOOT URL
    pitch.longDescription || "",                        // PROMOTION DETAILS (MARKET DRIVERS)
  ];
}

// ─── Deezer CSV (29 columns) ────────────────────────────────────────────────

const DEEZER_HEADERS = [
  "PROVIDER*",
  "LABEL*",
  "UPC*",
  "RELEASE DATE (DD/MM/YYYY)*",
  "RELEASE TIME (Default 00:00 UTC)",
  "MAIN ARTIST*",
  "FEATURING ARTISTS(if multiple, separate with a comma)",
  "TITLE*",
  "PRIORITY*",
  "CONF*",
  "TYPE*",
  "FOCUS TRACK*",
  "FOCUS TRACK ISRC*",
  "GENRE 1*(mini 1 / up to 3)",
  "GENRE 2",
  "GENRE 3",
  "LISTENING LINK*",
  "SUBGENRES",
  "MOODS",
  "EDITO TEAM*FR,BRA,DE,SP,PT,IT,JP,UK,BENELUX,GLOBAL(if multiple, separate with a comma)",
  "PHOTOS",
  "MARKETING DETAILS",
  "COMMENTS",
  "COUNTRY",
  "LOCATION",
  "ARTIST GENDER",
  "TRACK LANGUAGE",
  "TARGETED PLAYLIST",
];

function splitGenres(genreStr: string): [string, string, string] {
  if (!genreStr) return ["", "", ""];
  const genres = genreStr.split(",").map((g) => g.trim());
  return [genres[0] || "", genres[1] || "", genres[2] || ""];
}

// "Describe the song" values as entered in Cadence, translated to Deezer's
// expected TYPE* terminology. Values not listed here pass through unchanged.
const DEEZER_TYPE_TRANSLATIONS: Record<string, string> = {
  studio: "standard",
};

function translateDeezerType(songDescription: string): string {
  if (!songDescription) return "";
  return songDescription
    .split(",")
    .map((value) => {
      const trimmed = value.trim();
      return DEEZER_TYPE_TRANSLATIONS[trimmed.toLowerCase()] || trimmed;
    })
    .join(", ");
}

function buildDeezerRow(pitch: PitchData, labelName?: string): string[] {
  const deezer = pitch.DeezerInfo || {};
  const soundcloud = pitch.SoundCloudInfo || {};
  const spotify = pitch.SpotifyInfo || {};
  const [genre1, genre2, genre3] = splitGenres(deezer.subgenre || "");

  // Focus track: release title if config includes single, focusTrackName if album/EP
  const configLower = pitch.configuration?.toLowerCase() || "";
  const focusTrack = configLower.includes("single")
    ? pitch.releaseTitle
    : pitch.focusTrackName || "";

  // Marketing details: combine shortDescription + longDescription
  const marketingDetails = [pitch.shortDescription, pitch.longDescription]
    .filter(Boolean)
    .join(" - ");

  // Location: roster/Spotify field ("artist current town"), stored under
  // SpotifyInfo. Blank unless the artist is in the roster or Spotify was
  // pitched (the only two paths that populate this field).
  const location = spotify.artistCurrentTown || "";

  // LABEL*/PROVIDER* mapping depends on the pitching user's company type:
  // label-type companies never enter a label (it's their own), distributor-
  // type companies never enter a distributor (it's their own) — see
  // companyTypeClassification.ts for the category rules.
  const companyTypeCategory = classifyCompanyType(pitch.companyType);
  const associatedCompanyName = pitch.companyName || labelName || "";
  const distributorFieldValue = deezer.distributor || soundcloud.distributor || "";
  const labelFieldValue = deezer.label || soundcloud.label || "";

  let providerColumn: string;
  let labelColumn: string;
  if (companyTypeCategory === "label") {
    labelColumn = associatedCompanyName;
    providerColumn = distributorFieldValue;
  } else if (companyTypeCategory === "distributor") {
    labelColumn = labelFieldValue;
    providerColumn = associatedCompanyName;
  } else {
    labelColumn = labelFieldValue;
    providerColumn = distributorFieldValue;
  }

  return [
    providerColumn,                                     // PROVIDER*
    labelColumn,                                        // LABEL*
    pitch.releaseUPC || "",                             // UPC*
    formatDateDMY(pitch.releaseDate),                   // RELEASE DATE (DD/MM/YYYY)*
    "00:00",                                            // RELEASE TIME (Default 00:00 UTC)
    pitch.artistName || "",                             // MAIN ARTIST*
    pitch.featuredArtists || "",                        // FEATURING ARTISTS
    pitch.releaseTitle || "",                           // TITLE*
    deezer.priority || "",                              // PRIORITY*
    pitch.configuration || "",                          // CONF*
    translateDeezerType(pitch.songDescription || ""),   // TYPE*
    focusTrack || "",                                   // FOCUS TRACK*
    pitch.trackISRC || "",                              // FOCUS TRACK ISRC*
    genre1,                                             // GENRE 1*
    genre2,                                             // GENRE 2
    genre3,                                             // GENRE 3
    pitch.linkToListen || "",                           // LISTENING LINK*
    spotify.subgenre || "",                             // SUBGENRES (Spotify subgenres)
    pitch.songMood || "",                               // MOODS
    deezer.editorialTeam || "",                         // EDITO TEAM*
    pitch.promoPhotos || "",                            // PHOTOS
    marketingDetails,                                   // MARKETING DETAILS
    "",                                                 // COMMENTS
    "",                                                 // COUNTRY
    location,                                           // LOCATION
    "",                                                 // ARTIST GENDER (always blank)
    pitch.language || "",                               // TRACK LANGUAGE
    deezer.playlist || "",                              // TARGETED PLAYLIST
  ];
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Generate and download an Apple Music pitch CSV.
 * Returns true if download succeeded, false if blocked.
 */
export function downloadAppleCsv(pitch: PitchData): boolean {
  const row = buildAppleRow(pitch);
  const csvContent = buildCsv(APPLE_HEADERS, row);
  const date = formatDateYMD(new Date().toISOString());
  const fileName = `${sanitizeFileName(pitch.artistName)}_${sanitizeFileName(pitch.releaseTitle)}_ApplePitch_${date}.csv`;
  return triggerDownload(csvContent, fileName);
}

/**
 * Generate and download a Deezer pitch CSV.
 * Returns true if download succeeded, false if blocked.
 */
export function downloadDeezerCsv(pitch: PitchData, labelName?: string): boolean {
  const row = buildDeezerRow(pitch, labelName);
  const csvContent = buildCsv(DEEZER_HEADERS, row);
  const date = formatDateYMD(new Date().toISOString());
  const fileName = `${sanitizeFileName(pitch.artistName)}_${sanitizeFileName(pitch.releaseTitle)}_DeezerPitch_${date}.csv`;
  return triggerDownload(csvContent, fileName);
}
